import type { AlertStatus, AlertType, InvoiceStatus, Status, SubscriptionStatus, TrackingStatus } from '@vlm/api';
import { RoadSign, StatusPill, type SignName } from '@vlm/ui';
import { useT } from '../i18n';

/**
 * The road code for the legacy API's states. STOPPED has no ignition data behind it (the API
 * cannot tell idling from parked), so it takes the parked sign; red is never a status.
 */
export const TRACKING_SIGN: Record<TrackingStatus, SignName> = {
  MOVING: 'go',
  STOPPED: 'parked',
  OFFLINE: 'offline',
};

export const ALERT_SIGN: Record<AlertType, SignName> = {
  OVERSPEED: 'hazard',
  HARSH_DRIVING: 'hazard',
  IDLE_TIME: 'caution',
  GEOFENCE_ENTER: 'info',
  GEOFENCE_EXIT: 'info',
};

export const ALERT_STATUS_SIGN: Record<AlertStatus, SignName> = {
  NEW: 'hazard',
  ACKNOWLEDGED: 'caution',
  RESOLVED: 'go',
};

/** The one-way path an alert walks: NEW → ACKNOWLEDGED → RESOLVED. */
export const NEXT_ALERT_STATUS: Record<AlertStatus, AlertStatus | null> = {
  NEW: 'ACKNOWLEDGED',
  ACKNOWLEDGED: 'RESOLVED',
  RESOLVED: null,
};

export function TrackingPill({ status, speed }: { status: TrackingStatus; speed?: number | null }) {
  const { t, fmt } = useT();
  return (
    <StatusPill sign={TRACKING_SIGN[status]} detail={status === 'MOVING' && speed != null ? fmt.speed(speed) : undefined}>
      {t(`tracking.${status}`)}
    </StatusPill>
  );
}

export function EntityStatusPill({ status }: { status: Status }) {
  const { t } = useT();
  return <StatusPill sign={status === 'ACTIVE' ? 'go' : 'offline'}>{t(`status.${status}`)}</StatusPill>;
}

export function AlertStatusPill({ status }: { status: AlertStatus }) {
  const { t } = useT();
  return <StatusPill sign={ALERT_STATUS_SIGN[status]}>{t(`alertStatus.${status}`)}</StatusPill>;
}

/** The alert type with its sign — the sign carries the code, the words carry the meaning. */
export function AlertTypeLabel({ type, className }: { type: AlertType; className?: string }) {
  const { t } = useT();
  return (
    <span className={className ?? 'inline-flex items-center gap-2 font-semibold'}>
      <RoadSign sign={ALERT_SIGN[type]} size={18} />
      {t(`alertTypes.${type}`)}
    </span>
  );
}

const COMPASS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'] as const;

/** 45 → "NE 45°" (the words in the language on screen). */
export function useHeading() {
  const { t } = useT();
  return (degrees: number | null | undefined) => {
    if (degrees === null || degrees === undefined || !Number.isFinite(degrees)) return '—';
    const d = ((Math.round(degrees) % 360) + 360) % 360;
    return `${t(`directions.${COMPASS[Math.round(d / 45) % 8]}`)} ${d}°`;
  };
}

/** The plan's state, signed as the platform signs it: hazard is the payment that needs someone now; red is never a status. */
const SUBSCRIPTION_SIGN: Record<SubscriptionStatus | 'NONE', SignName> = { NONE: 'offline', PENDING: 'caution', ACTIVE: 'go', PAST_DUE: 'hazard', CANCELED: 'offline' };

export function SubscriptionPill({ status }: { status: SubscriptionStatus | 'NONE' }) {
  const { t } = useT();
  return <StatusPill sign={SUBSCRIPTION_SIGN[status]}>{t(`subscriptionStatus.${status}`)}</StatusPill>;
}

/** `paidOn`: printed beside "Paid" — the day the money arrived. */
export function InvoicePill({ status, overdue, paidOn }: { status: InvoiceStatus; overdue: boolean; paidOn?: string }) {
  const { t } = useT();
  if (overdue) return <StatusPill sign="hazard">{t('invoiceStatus.OVERDUE')}</StatusPill>;
  return (
    <StatusPill sign={status === 'PAID' ? 'go' : status === 'UNPAID' ? 'caution' : 'offline'} detail={status === 'PAID' ? paidOn : undefined}>
      {t(`invoiceStatus.${status}`)}
    </StatusPill>
  );
}
